// 原系統對話框的 React 版本（FC-4，資料驅動）。
//
// 資料來自 src/mocks/legacy-modals.ts（規則層）→ legacy-modals.generated.ts（資料層）
// → scripts/extract-legacy-modals.ts（從 legacy raw DOM 抽）。
//
// ---------------------------------------------------------------------------
// 為什麼不直接用 ModalFrame
// ---------------------------------------------------------------------------
// `ModalFrame`（src/components/layouts/ModalFrame.tsx:69）永遠會在 header 畫一顆
// 文字為 `×` 的關閉鈕（`:158-166`），而功能契約的 `modals.buttons[]` 是
// **「按鈕的可見文字，去空白、保序、去重」**（normalize-dom.ts:927-935），
// 也是 `signatureNoId` 的組成之一（FUNCTIONAL-CONTRACT.md:49／:103）。
// 也就是說「按鈕有哪幾顆、順序是什麼」是**被比對的內容**，不是版型自由。
//
// legacy 的按鈕順序實測有三種形態（`contracts/pages/*.json` 的 `modals[].buttons`）：
//   `["×","關閉","存檔"]`（最常見）、`["關閉","送出"]`（`#dialogApp`／`#dialogMail`，**沒有** ×）、
//   `["確定"]`（`#syncPeopleModal`，只有一顆）。
// 用 ModalFrame 會讓第二、三種被硬塞一顆 `×` 而永遠判不相符。
// 因此本元件自己畫外框，**按鈕完全依契約資料逐顆逐序畫**，一顆不多一顆不少。
//
// 焦點與鍵盤行為（Esc 關閉、focus trap、開啟時移入焦點、關閉時還原）逐條沿用
// `ModalFrame.tsx:84-131` 的實作，行為一致，只有「按鈕由誰決定」不同。
//
// ---------------------------------------------------------------------------
// 接線（criteria C5-d：對話框內欄位接線率 = 100%）
// ---------------------------------------------------------------------------
// 每個帶 `name` 的控制項都是受控元件：`value`／`checked` ＋ `onChange` → `values[name]`，
// 按下送出鈕時 `modalToPayload()` 組出 payload 交給 `onSubmit`，
// 呼叫端（LegacyContractModals）再送進 `useDataSource().create/update`。
// 兩類「沒有使用者改值事件」的控制項（hidden、沒有非空選項的 select）由
// 「值一定進 payload」保證，判準與理由寫在 src/mocks/legacy-modals.ts 檔頭。

import { type KeyboardEvent, useEffect, useId, useRef } from 'react';
import { FIELD_BASE_CLASS } from '@/components/base/shared-classes';
import {
  isModalFieldUserDrivable,
  type LegacyModalField,
  type LegacyModalSpec,
  modalCloseButtonIndex,
  modalFieldsToRender,
  modalOptionsFor,
  modalSubmitButtonIndex,
  modalToPayload,
} from '@/mocks/legacy-modals';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface LegacyContractModalProps {
  uiId: string;
  /** 資源名（雛形即 pageId），送出鈕的 data-effect 與 DataSource 呼叫都用它。 */
  resource: string;
  spec: LegacyModalSpec;
  open: boolean;
  values: Readonly<Record<string, string>>;
  onChange: (name: string, next: string) => void;
  onClose: () => void;
  /** 送出鈕被按下時呼叫，payload 由 `modalToPayload()` 組。 */
  onSubmit: (payload: Record<string, string>) => void;
}

function controlFor(
  uiId: string,
  spec: LegacyModalSpec,
  field: LegacyModalField,
  value: string,
  onChange: (name: string, next: string) => void,
) {
  const fieldUiId = `${uiId}-input-${field.name}`;
  const common = { name: field.name, required: field.required, id: field.idHint ?? undefined };

  if (field.hidden) {
    // hidden 承載原系統的參數（refType／action／planId…）。React 不對它派發 onChange，
    // 所以它的「接線」定義是「值一定進 payload」（見 legacy-modals.ts 檔頭）。
    return (
      <input
        {...common}
        type="hidden"
        data-ui-id={fieldUiId}
        value={value}
        onChange={(e) => {
          onChange(field.name, e.target.value);
        }}
      />
    );
  }

  if (field.type === 'select' || field.type === 'select-multiple') {
    const options = modalOptionsFor(spec, field);
    return (
      <select
        {...common}
        data-ui-id={fieldUiId}
        multiple={field.multiple}
        className={FIELD_BASE_CLASS}
        value={value}
        onChange={(e) => {
          onChange(field.name, e.target.value);
        }}
      >
        {/*
          **不補「請選擇」空選項**。契約的 `options.values` 是 legacy 那顆 select 的
          value 集合（`extract-legacy-search-forms.ts:157` 的 optionsAreEnum），
          多畫一個 `value=""` 會讓 React 側的值集合多一項而與 legacy 不同
          （實測：`#classAnnouncementDialogModal` 的 annoObj legacy 是 `["0"]`、
          補了空選項變成 `["","0"]`）。同一條紀律 FC-3 在
          `src/mocks/legacy-fields.ts` 檔頭已寫過：「不能為了數字塞選項」。
        */}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.text}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === 'textarea') {
    return (
      <textarea
        {...common}
        data-ui-id={fieldUiId}
        className={FIELD_BASE_CLASS}
        rows={3}
        value={value}
        onChange={(e) => {
          onChange(field.name, e.target.value);
        }}
      />
    );
  }

  if (field.type === 'checkbox' || field.type === 'radio') {
    const checkedValue = field.value === null || field.value === '' ? 'on' : field.value;
    return (
      <input
        {...common}
        type={field.type}
        data-ui-id={fieldUiId}
        value={checkedValue}
        checked={value === checkedValue}
        onChange={(e) => {
          onChange(field.name, e.target.checked ? checkedValue : '');
        }}
      />
    );
  }

  if (field.type === 'file') {
    // 上傳型：雛形不真的上傳，但控制項必須存在且帶 name（契約比 type）。
    //
    // **值取檔名而不是 `e.target.value`**：`<input type=file>` 的 `value` 由瀏覽器控制，
    // 讀出來是 `C:\fakepath\<檔名>`，jsdom 更是直接給空字串 —— 兩者都會讓這個欄位
    // 「使用者選了檔卻進不了 payload」，也就是 criteria C5-d 的接線斷掉。
    // `files[0].name` 是唯一在兩個環境都取得到的使用者輸入。
    return (
      <input
        {...common}
        type="file"
        data-ui-id={fieldUiId}
        className={FIELD_BASE_CLASS}
        onChange={(e) => {
          onChange(field.name, e.target.files?.[0]?.name ?? e.target.value);
        }}
      />
    );
  }

  return (
    <input
      {...common}
      type={field.type}
      data-ui-id={fieldUiId}
      className={FIELD_BASE_CLASS}
      value={value}
      onChange={(e) => {
        onChange(field.name, e.target.value);
      }}
    />
  );
}

export function LegacyContractModal({
  uiId,
  resource,
  spec,
  open,
  values,
  onChange,
  onClose,
  onSubmit,
}: LegacyContractModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // 開啟時把焦點移進對話框，關閉時還回原本的元素（同 ModalFrame.tsx:84-94）。
  useEffect(() => {
    if (!open) return;
    restoreRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    (panel?.querySelector<HTMLElement>(FOCUSABLE) ?? panel)?.focus();
    return () => {
      restoreRef.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const fields = modalFieldsToRender(spec);
  const closeIndex = modalCloseButtonIndex(spec);
  const submitIndex = modalSubmitButtonIndex(spec);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = panelRef.current;
    if (panel === null) return;
    const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) {
      event.preventDefault();
      return;
    }
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      data-ui-id={`${uiId}-backdrop`}
      // 【z-index：FC-5 E-Q1 裁決，對話框疊在 processing overlay 之下】
      // FC-4 R2 曾要求「對話框疊在 processing overlay 之上」；FC-5 查證
      // `common.js:443 showProcessing()` 的 spin.js 設定帶 `zIndex: 2e9`（`:475`），
      // 且 `:444-445` 鎖死 `#body-container` 的 `pointer-events`，語意是遮罩蓋住整頁
      // **含**對話框，方向相反。criteria E-Q1 裁決改回對齊原系統，`ProcessingOverlay.tsx`
      // 現在是 `z-[3000]`，本層維持 `z-[2050]`（與 `src/components/base/Modal.tsx:61` 一致，
      // 是 Bootstrap 3 `.modal { z-index: 1050 }` 慣例在本專案的落地值），
      // 兩者相對順序由 `src/test-support/modal-zindex.test.tsx` 把關。
      // 由 `src/test-support/modal-zindex.test.tsx` 以實際 computed z-index 比較把關。
      className="fixed inset-0 z-[2050] flex items-start justify-center overflow-y-auto bg-black/50 p-6"
    >
      <div
        ref={panelRef}
        data-ui-id={uiId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="w-full max-w-[900px] border-border border-t-[3px] bg-surface shadow-lg"
      >
        {/*
          標題：`normalize-dom.ts:844-857` 的 `modalTitle()` 先找 `.modal-title`，
          找不到才退到 h1–h6。React 側沒有 `.modal-title` 這個 class（class 不是契約內容），
          所以標題一定從這顆 <h2> 讀出來。
          legacy 有 55 個實例的 title 是空字串，此時本元件照樣畫空的 <h2>，
          抽取端得到的 title 同樣是 ''，兩側一致。
        */}
        <div
          data-ui-id={`${uiId}-header`}
          className="flex items-start gap-3 border-border border-b px-4 py-3"
        >
          <h2 id={titleId} data-ui-id={`${uiId}-title`} className="flex-1 font-bold text-[18px]">
            {spec.title}
          </h2>
        </div>

        <div data-ui-id={`${uiId}-body`} className="px-4 py-4">
          <div className="grid grid-cols-1 gap-3">
            {fields.map((field) => (
              <div key={field.name} className="flex flex-col gap-1">
                {field.hidden ? null : (
                  <span className="text-[13px] text-muted">
                    {field.label === '' ? field.name : field.label}
                  </span>
                )}
                {controlFor(uiId, spec, field, values[field.name] ?? '', onChange)}
              </div>
            ))}
          </div>

          {/*
            對話框內的表格：契約比的是 `<th>` 的文字序列（順序敏感）。
            資料列不畫——`FUNCTIONAL-CONTRACT.md:93`「不比資料列實際內容與筆數」，
            畫假資料列反而會多出 legacy 沒有的內容。
            橫向捲動由 overflow-x-auto 負責（criteria C7 的破版檢查項）。
          */}
          {spec.tableHeaders.length === 0 ? null : (
            <div data-ui-id={`${uiId}-table-scroll`} className="mt-4 overflow-x-auto">
              <table data-ui-id={`${uiId}-table`} className="w-full border-collapse text-[13px]">
                <thead>
                  <tr>
                    {spec.tableHeaders.map((h, i) => (
                      <th
                        // `table.headers` 在功能契約裡是**順序敏感的序列**
                        // （FUNCTIONAL-CONTRACT.md:136 判定表的 `table.headers` 列、
                        // `compare-contracts.ts` 的 lengthDiffers），不是集合。
                        // 兩件實測事實讓「用文字當 key」不可行：
                        //   ① 同一個對話框內**允許重複的表頭文字**（`normalize-dom.ts:937-941` 抽表頭時
                        //      刻意不去重，去重會讓欄數變少而整條序列判不相符）；
                        //   ② 這份清單是**靜態資料**（`legacy-modals.generated.ts`），
                        //      不會新增、刪除或重排，React 重用節點不會出錯。
                        // FC-5 複核更正（原理由②「允許空字串表頭」不成立，已刪除）：
                        // modal 表頭走的是 `normalize-dom.ts:941` 的
                        // `if (text) tableHeaders.push(text);`——**會過濾掉空字串**，
                        // 跟原本引用的 `page-columns.test.ts:49-56` 剛好相反（那支測的是
                        // `LEGACY_TABLE_HEADERS`，來自 `extract-table-headers.ts` 的**頁面層**表格，
                        // 跟 modal 表頭是不同程式碼路徑，引錯證據）。modal 表頭資料裡不會出現
                        // 空字串，所以「允許空字串表頭」從來不是這裡需要 index-as-key 的理由。
                        // 也就是說「第幾欄」就是這一格唯一的身分，索引正是正確的 key。
                        // 原本寫成 `${h.text}-${i}` 想避開這條規則，但 biome 對**含索引的模板字串**
                        // 一樣會報（R2 驗收由兩位驗證者各自抓到），而且那個 key 反而更誤導 ——
                        // 它看起來像「文字才是身分」，實際上仍然靠索引去重。改成明示豁免。
                        // biome-ignore lint/suspicious/noArrayIndexKey: 表頭沒有索引以外的穩定身分，理由見上
                        key={i}
                        data-ui-id={`${uiId}-th-${i}`}
                        className="whitespace-nowrap border-border border-b px-2 py-1 text-left"
                      >
                        {h.text}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody />
              </table>
            </div>
          )}
        </div>

        {/*
          按鈕：逐顆逐序照契約畫。角色（關閉／送出）由 legacy-modals.ts 指派，
          不改文字、不加也不減（signatureNoId 的組成之一）。
        */}
        <div
          data-ui-id={`${uiId}-footer`}
          className="flex flex-wrap justify-end gap-2 border-border border-t px-4 py-3"
        >
          {spec.buttons.map((button, index) => {
            const isClose = index === closeIndex;
            const isSubmit = index === submitIndex;
            return (
              <button
                key={button.text}
                type="button"
                data-ui-id={
                  isClose ? `${uiId}-close` : isSubmit ? `${uiId}-submit` : `${uiId}-btn-${index}`
                }
                // D-1／criteria C5-j：只有真的會呼叫 DataSource 的按鈕才帶 data-effect。
                // 這顆送出鈕的 onSubmit 由 LegacyContractModals 接到 dataSource.create()。
                // 關閉／取消型一律不帶（FC-3 C5-j 的反向哨兵）。
                {...(isSubmit ? { 'data-effect': `create:${resource}` } : {})}
                className={
                  isSubmit
                    ? 'border border-primary bg-primary px-3 py-1 text-[13px] text-on-primary'
                    : 'border border-border px-3 py-1 text-[13px]'
                }
                onClick={() => {
                  // 同一顆可能同時是送出與關閉（例：只有一顆「確定」的對話框）。
                  if (isSubmit) onSubmit(modalToPayload(spec, values));
                  if (isClose) onClose();
                }}
              >
                {button.text}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export { isModalFieldUserDrivable };
